import { useApi } from '@src/app/context/Api';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { SelectInputMulti } from '@uhg-abyss/web/ui/SelectInputMulti';
import { isEmpty, isFunction, isUndefined } from 'lodash';
import React, { useEffect, useMemo } from 'react';

import configuration from './includes/configuration.json';

/**
 * Field: TagSelection
 *
 * This field allows the user to select tags from a list of available system defined or user defined tags.
 *
 * @param form
 * @param handleChange
 * @param isDisabled
 * @param isRequired
 * @param label
 * @param model
 * @param showLabel
 * @param showSearch
 * @param showSelectAll
 * @param subText
 * @param systemDefinedTags
 * @param userDefinedTags
 * @param validators
 * @returns {Element}
 * @constructor
 */
export function TagSelection({
  form = {},
  handleChange = () => {},
  isDisabled = false,
  isRequired = true,
  label = configuration?.label,
  model = configuration?.model,
  showLabel = true,
  showSearch = true,
  showSelectAll = true,
  subText = configuration?.subText,
  systemDefinedTags = true,
  userDefinedTags = true,
  validators = {},
}) {
  const { useApiQuery } = useApi('Tag');
  const [ListTags, { data: tags, isFetching, isLoading }] = useApiQuery('ListTags');

  /**
   * Retrieve the list of tags.
   */
  useEffect(() => {
    if (isUndefined(tags)) {
      const payload = {
        page: 0,
        size: 9999,
        sort: 'code,asc',
        userDefined: null,
      };

      if (systemDefinedTags === false && userDefinedTags === true) {
        payload.userDefined = true;
      }

      if (systemDefinedTags === true || userDefinedTags === true) {
        ListTags(payload);
      }
    }
  }, [systemDefinedTags, userDefinedTags, tags]);

  /**
   * tagOptions
   *
   * Returns the tag options with categories for sections.
   *
   * @type {*[]}
   */
  const tagOptions = useMemo(() => {
    const theTags = [];

    if (!isUndefined(tags) && !isEmpty(tags)) {
      // tag category sections
      tags?.content?.forEach((tag) => {
        const theSection = theTags.find((theTag) => {
          return theTag?.section === tag?.categoryCode;
        });

        if (isUndefined(theSection)) {
          theTags.push({
            items: [],
            section: tag?.categoryCode,
          });
        }
      });

      // tag options within sections
      tags?.content?.forEach((tag) => {
        const theSection = theTags.find((theTag) => {
          return theTag?.section === tag?.categoryCode;
        });

        if (!isUndefined(theSection)) {
          theSection?.items.push({
            label: `${tag?.code} (${tag?.categoryCode})`,
            searchAttributes: {
              categoryCode: tag?.categoryCode,
              categoryDescription: tag?.categoryDesc,
              tagCode: tag?.code,
              tagDescription: tag?.description,
            },
            value: tag?.tag,
          });
        }
      });
    }

    return theTags;
  }, [tags]);

  /**
   * fieldProps
   *
   * the props to pass to the SelectInputMulti component
   *
   * @type {{criteria: {}, hideLabel: boolean, isSearchable: boolean, keys: {}, label: string, maxListHeight: string,
   *   model: string, options: *[], placeholder: string, selectAll: boolean, subText: string, validators: {required:
   *   string}, isLoading: boolean, onChange: function, isDisabled: boolean}}
   */
  const fieldProps = useMemo(() => {
    const theProps = {
      ...configuration,
      isDisabled,
      isLoading: isLoading || isFetching,
      onChange: (values) => {
        if (isFunction(handleChange)) {
          handleChange(values);
        }
        form?.validate(
          model,
          () => {},
          () => {}
        );
      },
      options: tagOptions,
    };

    if (!isEmpty(validators)) {
      theProps.validators = validators;
    }

    if (label !== configuration?.label) {
      theProps.label = label;
    }

    if (subText !== configuration?.subText) {
      theProps.subText = subText;
    }

    if (!showLabel) {
      theProps.hideLabel = true;
    }

    if (!showSearch) {
      theProps.isSearchable = false;
    }

    if (!showSelectAll) {
      theProps.selectAll = false;
    }

    if (!isRequired) {
      theProps.validators = {
        required: false,
      };
    }

    if (!isEmpty(model) && model !== configuration?.model) {
      theProps.model = model;
    }

    return theProps;
  }, [
    configuration,
    handleChange,
    isDisabled,
    isFetching,
    isLoading,
    isRequired,
    label,
    showLabel,
    showSearch,
    showSelectAll,
    subText,
    tagOptions,
    validators,
  ]);

  return (
    <ErrorHandler location="src/entities/Tag/ui/components/fields/TagSelection/TagSelection.jsx">
      <SelectInputMulti {...fieldProps} />
    </ErrorHandler>
  );
}
